import { ScrollView, Platform } from "react-native";
import { View, Text } from "@src/components";
import {
  showSuccessToast,
  showErrorToast,
  showWarningToast,
  showInfoToast,
  showToastWithActions,
  showCustomToast,
  generateIdToast,
} from "@src/utils/toast";
import { Button } from "@src/components/Button";
import { toast } from "@src/lib/sonner";

export default function ToastDemoScreen() {
  const handleSuccessToast = () => {
    showSuccessToast({ message: "Operation completed successfully!" });
  };

  const handleSuccessToastWithDescription = () => {
    showSuccessToast({ message: "Profile updated", description: "Your changes have been saved." });
  };

  const handleErrorToast = () => {
    showErrorToast({ message: "Something went wrong" });
  };

  const handleErrorToastWithDescription = () => {
    showErrorToast({ message: "Failed to save", description: "Please check your connection and try again." });
  };

  const handleWarningToast = () => {
    showWarningToast({ message: "Warning: Low battery" });
  };

  const handleWarningToastWithDescription = () => {
    showWarningToast({ message: "Storage almost full", description: "You have less than 1GB of storage remaining." });
  };

  const handleInfoToast = () => {
    showInfoToast({ message: "New feature available" });
  };

  const handleInfoToastWithDescription = () => {
    showInfoToast({ message: "Update available", description: "Version 2.0 includes new features and improvements." });
  };

  const handleToastWithAction = () => {
    showToastWithActions({
      type: "info",
      message: "Item deleted",
      description: "You can undo this action",
      actions: {
        actionLabel: "Undo",
        onAction: () => {
          toast.dismiss();
          showSuccessToast({ message: "Item restored" });
        },
      },
    });
  };

  const handleCustomToastWithUndoAction = () => {
    showCustomToast({
      type: "success",
      message: "Item restored",
      description: "You can undo this action",
      actions: {
        actionLabel: "Undo",
        onAction: () => {
          toast.dismiss();
          showSuccessToast({ message: "Item restored" });
        },
      },
    });
  };

  const handleToastWithActionButton = () => {
    showSuccessToast({
      message: "Settings saved",
      description: "Your preferences have been updated",
      options: {
        actions: {
          actionLabel: "View",
          onAction: () => {
            toast.dismiss();
            showInfoToast({ message: "Navigating to settings..." });
          },
        },
      },
    });
  };

  const handleCustomToastWithCustomAction = () => {
    showCustomToast({
      type: "success",
      message: "Settings saved",
      description: "Your preferences have been updated",
      actions: {
        actionLabel: "View",
        onAction: () => {
          toast.dismiss();
          showInfoToast({ message: "Navigating to settings..." });
        },
      },
    });
  };

  const handleCustomDuration = () => {
    showInfoToast({
      message: "This toast will stay longer",
      description: "It has a custom duration of 8 seconds",
      options: { duration: 8000 },
    });
  };

  const handleCustomDurationWithCustomDuration = () => {
    showCustomToast({
      type: "info",
      message: "This toast will stay longer",
      description: "It has a custom duration of 8 seconds",
      duration: 8000,
    });
  };
  const handleCustomSuccessToast = () => {
    showCustomToast({
      type: "success",
      message: "Operation completed successfully!",
    });
  };

  const handleCustomSuccessToastWithDescription = () => {
    showCustomToast({
      type: "success",
      message: "Profile updated",
      description: "Your changes have been saved.",
    });
  };

  const handleCustomErrorToast = () => {
    showCustomToast({
      type: "error",
      message: "Something went wrong",
    });
  };

  const handleCustomErrorToastWithDescription = () => {
    showCustomToast({
      type: "error",
      message: "Failed to save",
      description: "Please check your connection and try again.",
    });
  };

  const handleCustomWarningToast = () => {
    showCustomToast({
      type: "warning",
      message: "Warning: Low battery",
    });
  };

  const handleCustomWarningToastWithDescription = () => {
    showCustomToast({
      type: "warning",
      message: "Storage almost full",
      description: "You have less than 1GB of storage remaining.",
    });
  };

  const handleCustomInfoToast = () => {
    showCustomToast({
      type: "info",
      message: "New feature available",
    });
  };

  const handleCustomInfoToastWithDescription = () => {
    showCustomToast({
      type: "info",
      message: "Update available",
      description: "Version 2.0 includes new features and improvements.",
    });
  };

  const handleToastWithActionsButtons = () => {
    const id = generateIdToast();
    showToastWithActions({
      type: "info",
      message: "Item deleted",
      description: "You can undo this action",
      actions: {
        actionLabel: "Undo",
        onAction: () => {
          toast.dismiss(id);
          showSuccessToast({ message: "Item restored" });
        },
        cancelLabel: "Cancel",
        onCancel: () => {
          toast.dismiss(id);
          showErrorToast({ message: "Item not deleted" });
        },
      },
      options: {
        id,
      },
    });
  };

  const handleCustomToastWithActionsButtons = () => {
    const id = generateIdToast();
    showCustomToast({
      id,
      type: "info",
      message: "Item deleted",
      description: "You can undo this action",
      actions: {
        actionLabel: "Undo",
        onAction: () => {
          toast.dismiss(id);
          showSuccessToast({ message: "Item restored" });
        },
        cancelLabel: "Cancel",
        onCancel: () => {
          toast.dismiss(id);
          showErrorToast({ message: "Item not deleted" });
        },
      },
    });
  };

  const handleToastWithCloseButton = () => {
    showInfoToast({
      message: "Item deleted",
      description: "You can undo this action",
      options: {
        closeButton: true,
      },
    });
  };

  const handleCustomToastWithCloseButton = () => {
    showCustomToast({
      type: "info",
      message: "Item deleted",
      description: "You can undo this action",
      closeButton: true,
    });
  };

  const handleToastNotDismissible = () => {
    showErrorToast({
      message: "Item deleted",
      description: "You can undo this action",
      options: { dismissible: false, duration: Infinity },
    });
  };
  const handleCustomToastNotDismissible = () => {
    showCustomToast({
      type: "error",
      message: "Item deleted",
      description: "You can undo this action",
      dismissible: false,
      duration: Infinity,
    });
  };

  const handleToggleToastDismissible = () => {
    toast.dismiss();
  };

  const handleToastWithProgressBar = () => {
    showCustomToast({
      type: "success",
      message: "Success",
      description: "This is a success toast with a progress bar",
      progress: true,
      duration: 4000, // 4000 is the default duration
    });
  };

  return (
    <View flex={1} bg="background">
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20, paddingBottom: 40 }}>
        <View mb="6xl">
          <Text textStyle="4xl" fontWeight="bold">
            Toast Component Demo
          </Text>
          <Text textStyle="sm/regular" color="textMuted" mt="sm">
            Tap the buttons below to see different toast notifications
          </Text>
        </View>

        <View mb="8xl">
          <Text textStyle="xl" fontWeight="bold" mb="4xl">
            Success Toasts
          </Text>

          <View gap="md" mb="4xl">
            <Button
              label="Show Success Toast"
              variant="primary"
              variantColor="success"
              onPress={handleSuccessToast}
              textStyle="md/semibold"
            />

            <Button
              label="Success Toast with Description"
              variant="primary"
              variantColor="success"
              onPress={handleSuccessToastWithDescription}
              textStyle="md/semibold"
            />

            <Button
              label="Show Custom Success Toast"
              variant="primary"
              variantColor="success"
              onPress={handleCustomSuccessToast}
              textStyle="md/semibold"
            />

            <Button
              label="Show Custom Success Toast with Description"
              variant="primary"
              variantColor="success"
              onPress={handleCustomSuccessToastWithDescription}
              textStyle="md/semibold"
            />
          </View>
        </View>

        <View mb="8xl">
          <Text textStyle="xl" fontWeight="bold" mb="4xl">
            Error Toasts
          </Text>

          <View gap="md" mb="4xl">
            <Button
              label="Show Error Toast"
              variant="primary"
              variantColor="danger"
              onPress={handleErrorToast}
              textStyle="md/semibold"
            />
            <Button
              label="Error Toast with Description"
              variant="primary"
              variantColor="danger"
              onPress={handleErrorToastWithDescription}
              textStyle="md/semibold"
            />
            <Button
              label="Show Custom Error Toast"
              variant="primary"
              variantColor="danger"
              onPress={handleCustomErrorToast}
              textStyle="md/semibold"
            />
            <Button
              label="Show Custom Error Toast with Description"
              variant="primary"
              variantColor="danger"
              onPress={handleCustomErrorToastWithDescription}
              textStyle="md/semibold"
            />
          </View>
        </View>

        <View mb="8xl">
          <Text textStyle="xl" fontWeight="bold" mb="4xl">
            Warning Toasts
          </Text>

          <View gap="md" mb="4xl">
            <Button
              label="Show Warning Toast"
              variant="primary"
              variantColor="warning"
              onPress={handleWarningToast}
              textStyle="md/semibold"
            />
            <Button
              label="Warning Toast with Description"
              variant="primary"
              variantColor="warning"
              onPress={handleWarningToastWithDescription}
              textStyle="md/semibold"
            />
            <Button
              label="Show Custom Warning Toast"
              variant="primary"
              variantColor="warning"
              onPress={handleCustomWarningToast}
              textStyle="md/semibold"
            />
            <Button
              label="Show Custom Warning Toast with Description"
              variant="primary"
              variantColor="warning"
              onPress={handleCustomWarningToastWithDescription}
              textStyle="md/semibold"
            />
          </View>
        </View>

        <View mb="8xl">
          <Text textStyle="xl" fontWeight="bold" mb="4xl">
            Info Toasts
          </Text>

          <View gap="md" mb="4xl">
            <Button
              label="Show Info Toast"
              variant="primary"
              variantColor="blue"
              onPress={handleInfoToast}
              textStyle="md/semibold"
            />
            <Button
              label="Info Toast with Description"
              variant="primary"
              variantColor="blue"
              onPress={handleInfoToastWithDescription}
              textStyle="md/semibold"
            />
            <Button
              label="Show Custom Info Toast"
              variant="primary"
              variantColor="blue"
              onPress={handleCustomInfoToast}
              textStyle="md/semibold"
            />
            <Button
              label="Show Custom Info Toast with Description"
              variant="primary"
              variantColor="blue"
              onPress={handleCustomInfoToastWithDescription}
              textStyle="md/semibold"
            />
          </View>
        </View>

        <View mb="8xl">
          <Text textStyle="xl" fontWeight="bold" mb="4xl">
            Toast with Action Button
          </Text>

          <View gap="md" mb="4xl">
            <Button
              label="Toast with Undo Action"
              variant="primary"
              variantColor="navy"
              onPress={handleToastWithAction}
              textStyle="md/semibold"
            />
            <Button
              label="Custom Toast with Undo Action"
              variant="primary"
              variantColor="navy"
              onPress={handleCustomToastWithUndoAction}
              textStyle="md/semibold"
            />
            <Button
              label="Toast with Custom Action"
              variant="primary"
              variantColor="navy"
              onPress={handleToastWithActionButton}
              textStyle="md/semibold"
            />
            <Button
              label="Custom Toast with Custom Action"
              variant="primary"
              variantColor="navy"
              onPress={handleCustomToastWithCustomAction}
              textStyle="md/semibold"
            />
            <Button
              label="Toast with Actions Buttons"
              variant="primary"
              variantColor="navy"
              onPress={handleToastWithActionsButtons}
              textStyle="md/semibold"
            />
            <Button
              label="Custom Toast with Actions Buttons"
              variant="primary"
              variantColor="navy"
              onPress={handleCustomToastWithActionsButtons}
              textStyle="md/semibold"
            />
          </View>
        </View>

        <View mb="8xl">
          <Text textStyle="xl" fontWeight="bold" mb="4xl">
            Custom Options
          </Text>

          <View gap="md" mb="4xl">
            <Button
              label="Toast with Custom Duration (8s)"
              variant="primary"
              variantColor="cyan"
              onPress={handleCustomDuration}
              textStyle="md/semibold"
            />
            <Button
              label="Custom Toast with Custom Duration (8s)"
              variant="primary"
              variantColor="cyan"
              onPress={handleCustomDurationWithCustomDuration}
              textStyle="md/semibold"
            />
          </View>
        </View>

        <View mb="8xl">
          <Text textStyle="xl" fontWeight="bold" mb="4xl">
            Toast with Close Button
          </Text>

          <View gap="md" mb="4xl">
            <Button
              label="Toast with Close Button"
              variant="primary"
              variantColor="teal"
              onPress={handleToastWithCloseButton}
              textStyle="md/semibold"
            />
            <Button
              label="Custom Toast with Close Button"
              variant="primary"
              variantColor="teal"
              onPress={handleCustomToastWithCloseButton}
              textStyle="md/semibold"
            />
          </View>
        </View>

        <View mb="8xl">
          <Text textStyle="xl" fontWeight="bold" mb="4xl">
            Toast with Progress Bar
          </Text>

          <View gap="md" mb="4xl">
            <Button
              label="Toast with Progress Bar"
              variant="primary"
              variantColor="red"
              onPress={handleToastWithProgressBar}
              textStyle="md/semibold"
            />
          </View>
        </View>

        <View mb="8xl">
          <Text textStyle="xl" fontWeight="bold" mb="4xl">
            Non-Dismissible Toasts
          </Text>

          <View gap="md" mb="4xl">
            <Button
              label="Non-Dismissible Toast"
              variant="primary"
              variantColor="grey"
              onPress={handleToastNotDismissible}
              textStyle="md/semibold"
            />
            <Button
              label="Custom Non-Dismissible Toast"
              variant="primary"
              variantColor="grey"
              onPress={handleCustomToastNotDismissible}
              textStyle="md/semibold"
            />
            <Button
              label="Dismiss All Non-Dismissible Toasts"
              variant="primary"
              variantColor="grey"
              onPress={handleToggleToastDismissible}
              textStyle="md/semibold"
            />
          </View>
        </View>
      </ScrollView>
    </View>
  );
}
